import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  deletePushSubscription,
  fetchPushBootstrap,
  upsertPushSubscription,
} from '@/api/teleopApi';
import type { PushBootstrapResponse, PushSubscriptionSummary } from '@/contracts';
import type { PwaInstallState } from '@/lib/pwa';
import {
  buildUpsertPushSubscriptionRequest,
  ensureBrowserPushSubscription,
  getOrCreatePushAppInstallId,
  isWebPushSupported,
  readBrowserPushPermissionState,
  readBrowserPushSubscription,
  requestBrowserPushPermission,
  resolvePushDeviceLabel,
  type BrowserPushPermissionState,
} from '@/lib/push';
import { logWarn } from '@/lib/logger';

export type PushSetupState = {
  appInstallId: string;
  bootstrap: PushBootstrapResponse | null;
  currentDeviceSubscription: PushSubscriptionSummary | null;
  permissionState: BrowserPushPermissionState;
  isBrowserSupported: boolean;
  isBusy: boolean;
  isLoading: boolean;
  errorMessage: string | null;
  webPushEnabled: boolean;
};

type UsePushSetupInput = {
  getToken?: () => Promise<string | null>;
  installState: PwaInstallState;
};

type UsePushSetupResult = {
  pushSetupState: PushSetupState;
  enablePushSetup: () => Promise<void>;
  disablePushSetup: () => Promise<void>;
  refreshPushSetup: () => Promise<void>;
};

const buildInitialPushSetupState = (appInstallId: string): PushSetupState => {
  return {
    appInstallId,
    bootstrap: null,
    currentDeviceSubscription: null,
    permissionState: readBrowserPushPermissionState(),
    isBrowserSupported: isWebPushSupported(),
    isBusy: false,
    isLoading: true,
    errorMessage: null,
    webPushEnabled: false,
  };
};

const findCurrentDeviceSubscription = (
  bootstrap: PushBootstrapResponse | null,
  appInstallId: string
): PushSubscriptionSummary | null => {
  if (bootstrap === null) {
    return null;
  }

  const matchingSubscription =
    bootstrap.subscriptions.find((subscription) => {
      const hasMatchingAppInstallId = subscription.app_install_id === appInstallId;
      const isActiveSubscription = subscription.disabled_at === null;
      return hasMatchingAppInstallId && isActiveSubscription;
    }) ?? null;
  return matchingSubscription;
};

export const usePushSetup = ({
  getToken,
  installState,
}: UsePushSetupInput): UsePushSetupResult => {
  const appInstallId = useMemo(() => getOrCreatePushAppInstallId(), []);
  const [pushSetupState, setPushSetupState] = useState<PushSetupState>(() =>
    buildInitialPushSetupState(appInstallId)
  );

  const refreshPermissionState = useCallback(() => {
    const nextPermissionState = readBrowserPushPermissionState();
    const nextIsBrowserSupported = isWebPushSupported();
    setPushSetupState((currentState) => ({
      ...currentState,
      permissionState: nextPermissionState,
      isBrowserSupported: nextIsBrowserSupported,
    }));
  }, []);

  const refreshPushSetup = useCallback(async () => {
    if (!getToken) {
      setPushSetupState((currentState) => ({
        ...currentState,
        isLoading: false,
      }));
      return;
    }

    setPushSetupState((currentState) => ({
      ...currentState,
      isLoading: true,
      errorMessage: null,
    }));

    try {
      const token = await getToken();
      if (!token) {
        setPushSetupState((currentState) => ({
          ...currentState,
          bootstrap: null,
          currentDeviceSubscription: null,
          isLoading: false,
        }));
        refreshPermissionState();
        return;
      }

      const bootstrap = await fetchPushBootstrap(token);
      const currentDeviceSubscription = findCurrentDeviceSubscription(
        bootstrap,
        appInstallId
      );
      setPushSetupState((currentState) => ({
        ...currentState,
        bootstrap,
        currentDeviceSubscription,
        isLoading: false,
        webPushEnabled: bootstrap.web_push_enabled,
      }));
      refreshPermissionState();
    } catch (error) {
      setPushSetupState((currentState) => ({
        ...currentState,
        isLoading: false,
        errorMessage:
          error instanceof Error
            ? error.message
            : 'Push setup bootstrap failed.',
      }));
      refreshPermissionState();
    }
  }, [appInstallId, getToken, refreshPermissionState]);

  useEffect(() => {
    void refreshPushSetup();
  }, [refreshPushSetup]);

  const enablePushSetup = useCallback(async () => {
    setPushSetupState((currentState) => ({
      ...currentState,
      isBusy: true,
      errorMessage: null,
    }));

    try {
      if (!installState.isInstalled) {
        throw new Error('Install Play Claw first.');
      }

      if (!isWebPushSupported()) {
        throw new Error('This browser does not support web push.');
      }

      if (!getToken) {
        throw new Error('Missing auth token for push setup.');
      }

      const token = await getToken();
      if (!token) {
        throw new Error('Missing auth token for push setup.');
      }

      const bootstrap = await fetchPushBootstrap(token);
      const vapidPublicKey = bootstrap.vapid_public_key;
      if (!bootstrap.web_push_enabled || !vapidPublicKey) {
        throw new Error('Queue alert setup is not available right now.');
      }

      const nextPermissionState = await requestBrowserPushPermission();
      setPushSetupState((currentState) => ({
        ...currentState,
        permissionState: nextPermissionState,
      }));

      if (nextPermissionState !== 'granted') {
        if (nextPermissionState === 'denied') {
          throw new Error(
            'Notifications are blocked for Play Claw on this device.'
          );
        }
        return;
      }

      const browserSubscription = await ensureBrowserPushSubscription(
        vapidPublicKey
      );
      const deviceLabel = resolvePushDeviceLabel(installState.platformFamily);
      const requestPayload = buildUpsertPushSubscriptionRequest({
        appInstallId,
        subscription: browserSubscription,
        permissionState: nextPermissionState,
        platformFamily: installState.platformFamily,
        deviceLabel,
      });
      await upsertPushSubscription(requestPayload, token);
      await refreshPushSetup();
    } catch (error) {
      setPushSetupState((currentState) => ({
        ...currentState,
        errorMessage:
          error instanceof Error
            ? error.message
            : 'Failed to enable queue alerts.',
      }));
    } finally {
      setPushSetupState((currentState) => ({
        ...currentState,
        isBusy: false,
      }));
      refreshPermissionState();
    }
  }, [appInstallId, getToken, installState, refreshPermissionState, refreshPushSetup]);

  const disablePushSetup = useCallback(async () => {
    const currentDeviceSubscription = pushSetupState.currentDeviceSubscription;
    if (currentDeviceSubscription === null) {
      return;
    }

    setPushSetupState((currentState) => ({
      ...currentState,
      isBusy: true,
      errorMessage: null,
    }));

    try {
      if (!getToken) {
        throw new Error('Missing auth token for push setup.');
      }

      const token = await getToken();
      if (!token) {
        throw new Error('Missing auth token for push setup.');
      }

      await deletePushSubscription(
        currentDeviceSubscription.subscription_id,
        token
      );

      const browserSubscription = await readBrowserPushSubscription();
      if (browserSubscription !== null) {
        try {
          await browserSubscription.unsubscribe();
        } catch (error) {
          logWarn(
            'push.subscription.unsubscribe_failed',
            'Browser push unsubscribe failed',
            {
              error: String(error),
              subscription_id: currentDeviceSubscription.subscription_id,
            }
          );
        }
      }

      await refreshPushSetup();
    } catch (error) {
      setPushSetupState((currentState) => ({
        ...currentState,
        errorMessage:
          error instanceof Error
            ? error.message
            : 'Failed to disable queue alerts.',
      }));
    } finally {
      setPushSetupState((currentState) => ({
        ...currentState,
        isBusy: false,
      }));
      refreshPermissionState();
    }
  }, [getToken, pushSetupState.currentDeviceSubscription, refreshPermissionState, refreshPushSetup]);

  return {
    pushSetupState,
    enablePushSetup,
    disablePushSetup,
    refreshPushSetup,
  };
};
